import { works } from "@/mocks/works";
import { reviews, reviewTopics } from "@/mocks/reviews";
import { newsItems } from "@/mocks/news";
import type { Review, Work } from "@/types/content";

export const genreOptions = ["剧情", "悬疑", "科幻", "犯罪", "动作", "爱情", "冒险", "古装", "政治", "惊悚", "医疗"];
export const regionOptions = ["中国大陆", "美国", "英国", "日本", "韩国", "中国台湾"];
export const yearOptions = [2026, 2025, 2024];
export const sortOptions = [
  { value: "heat", label: "按热度" },
  { value: "rating", label: "按评分" },
  { value: "time", label: "按更新时间" },
];

export function getWorkById(id: string): Work | undefined {
  return works.find((w) => w.id === id);
}

export function getWorksByType(type: string): Work[] {
  return works.filter((w) => w.type === type);
}

export function getReviewsByWork(workId: string): Review[] {
  return reviews.filter((r) => r.workId === workId);
}

export function getTopicById(topicId: string): (typeof reviewTopics)[number] | undefined {
  return reviewTopics.find((t) => t.id === topicId);
}

export function getAllTopics() {
  return reviewTopics;
}

export function getReviewsByTopic(topicId: string): Review[] {
  return reviews.filter((r) => r.topicId === topicId);
}

export function getWorksByIds(ids: string[]): Work[] {
  return ids.map((id) => getWorkById(id)).filter((w): w is Work => Boolean(w));
}

export function getRelatedWorks(work: Work, limit = 4): Work[] {
  return works
    .filter((w) => w.id !== work.id)
    .map((w) => {
      const genreScore = w.genres.filter((g) => work.genres.includes(g)).length * 3;
      const regionScore = w.region === work.region ? 1 : 0;
      const directorScore = w.director === work.director ? 2 : 0;
      return { w, score: genreScore + regionScore + directorScore };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || b.w.heat - a.w.heat)
    .slice(0, limit)
    .map((item) => item.w);
}

export interface WorkFilters {
  type?: string;
  genre?: string;
  region?: string;
  year?: number | null;
  sort?: string;
}

export function filterWorks(filters: WorkFilters): Work[] {
  let list = works.slice();
  if (filters.type) list = list.filter((w) => w.type === filters.type);
  if (filters.genre) list = list.filter((w) => w.genres.includes(filters.genre as string));
  if (filters.region) list = list.filter((w) => w.region === filters.region);
  if (filters.year) list = list.filter((w) => w.year === filters.year);

  const sort = filters.sort || "heat";
  list.sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "time") return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
    return b.heat - a.heat;
  });
  return list;
}

export interface SearchResult {
  kind: "work" | "review";
  id: string;
  title: string;
  typeLabel: string;
  summary: string;
  link: string;
}

export function searchContent(keyword: string, category = "all"): SearchResult[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const results: SearchResult[] = [];

  if (category === "all" || category === "movie" || category === "series") {
    works
      .filter((w) => (category === "all" ? true : w.type === category))
      .filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.director.toLowerCase().includes(kw) ||
          w.genres.some((g) => g.includes(kw)) ||
          w.cast.some((c) => c.name.toLowerCase().includes(kw)),
      )
      .forEach((w) => {
        results.push({
          kind: "work",
          id: w.id,
          title: w.title,
          typeLabel: w.type === "movie" ? "电影" : "电视剧",
          summary: `${w.year} · ${w.region} · ${w.genres.join(" / ")}`,
          link: `/detail/${w.id}`,
        });
      });
  }

  if (category === "all" || category === "review") {
    reviews
      .filter((r) => r.title.toLowerCase().includes(kw) || r.excerpt.toLowerCase().includes(kw) || r.author.includes(kw))
      .forEach((r) => {
        results.push({
          kind: "review",
          id: r.id,
          title: r.title,
          typeLabel: "影评",
          summary: r.excerpt,
          link: `/review/topic/${r.topicId}`,
        });
      });
  }

  return results;
}

export function getFeaturedWorks(type: string, limit = 6): Work[] {
  return filterWorks({ type, sort: "heat" }).slice(0, limit);
}

export function getLatestNews(limit = 5) {
  return newsItems.slice(0, limit);
}

export function getFeaturedTopics(limit = 3) {
  return reviewTopics.slice(0, limit);
}

export function getReviewsWithWork(limit = 3) {
  return reviews.slice(0, limit).map((r) => ({ review: r, work: getWorkById(r.workId) }));
}